/**
 * Fixed-timestep simulation clock.
 *
 * The simulation always advances in exact STEP_MS increments, independent of
 * the display's frame rate. Rendering interpolates between steps using `alpha`.
 * This class is pure (no requestAnimationFrame) so it can be unit-tested and
 * reused for headless replays.
 */
export const TICKS_PER_SECOND = 60;
export const STEP_MS = 1000 / TICKS_PER_SECOND;

/** Cap on catch-up steps per frame, so a backgrounded tab doesn't spiral. */
export const MAX_STEPS_PER_FRAME = 5;

export class FixedStepClock {
  private accumulator = 0;
  tick = 0;

  /**
   * Feed real elapsed time; returns how many simulation steps to run now.
   * Excess time beyond MAX_STEPS_PER_FRAME is dropped (the game slows down
   * instead of freezing).
   */
  advance(elapsedMs: number): number {
    this.accumulator += Math.max(0, elapsedMs);
    let steps = Math.floor(this.accumulator / STEP_MS);
    if (steps > MAX_STEPS_PER_FRAME) {
      steps = MAX_STEPS_PER_FRAME;
      this.accumulator = 0;
    } else {
      this.accumulator -= steps * STEP_MS;
    }
    this.tick += steps;
    return steps;
  }

  /** Interpolation factor in [0, 1) between the last and next step. */
  get alpha(): number {
    return this.accumulator / STEP_MS;
  }
}
